import { memo, useEffect, useState } from "react";
import {
  Modal,
  Box,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from "../../../api/axios";
import { BASE_URL } from "../../../constants/apiConstants";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 368,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
  borderRadius: '6px',
  outline: 'none'
};

interface Role {
  roleid: number;
  rolename: string;
  isactive: string;
}

interface InfoModalProps {
  open: boolean;
  handleClose: () => void;
  handleOk?: () => void;
  title?: string;
  closeText?: string;
  submitText?: string;
  singleBtn?: boolean;
  roleData?: Role;
  mode?: 'add' | 'edit' | 'view';
  roleList?: Role[];
}

const AddRoleModal: React.FC<InfoModalProps> = ({
  open,
  handleClose,
  handleOk,
  title = "Add / Edit Role",
  closeText = "Cancel",
  submitText = "Submit",
  singleBtn,
  roleData,
  mode = 'add',
  roleList = []
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isActive, setIsActive] = useState<string>("");
  const navigate = useNavigate();
  useEffect(() => {
    if (roleData) {
      setInputValue(roleData.rolename || "");
      setIsActive(roleData.isactive === "Y" ? "yes" : "no");
    } else {
      setInputValue("");
      setIsActive("");
    }
  }, [roleData]);

  const handleSubmit = async () => {
    const trimmedName = inputValue.trim();

    if (!trimmedName || !isActive) {
      toast("Please fill in all required fields.", { type: "warning" });
      return;
    }

    const nameChanged = trimmedName !== roleData?.rolename?.trim();
    const roleNamePattern = /^[A-Za-z][A-Za-z0-9 _-]*$/;

    if (nameChanged && !roleNamePattern.test(trimmedName)) {
      toast("Role name must start with a letter and can only include letters, numbers, spaces, underscores (_) or hyphens (-).", {
        type: "error",
      });
      return;
    }

    const isDuplicate = roleList.some(
      (role) =>
        role.rolename.trim().toLowerCase() === trimmedName.toLowerCase() &&
        role.roleid !== roleData?.roleid
    );

    if (isDuplicate) {
      toast("Role name already exists. Please choose a different name.", { type: "error" });
      return;
    }

    const payload = {
      roleid: roleData?.roleid || 0,
      rolename: trimmedName,
      isactive: isActive === "yes" ? "Y" : "N",
      createdby: localStorage.getItem("userid"),
    };

    try {
      await axiosInstance.post(`${BASE_URL}/api/v1/Role/SaveRole`, payload);
      toast(`Role configuration ${payload.roleid !== 0 ? "updated" : "added"} successfully`, { type: "success" });
      if (handleOk) handleOk();
      handleClose();
      navigate(`/roleconfiguration`)      
    } catch (err) {
      toast(`Failed to ${payload.roleid !== 0 ? "update" : "add"} role configuration`, { type: "error" });
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      className="modal"
    >
      <Box sx={modalStyle} className="modal">
        <div className="modal-header flex justify-content-between align-items-center">
          {title && <h3 className="modal-title">{title}</h3>}
          <button className="close" onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          <div className="input-wrap mb-12">
            <label className="mb-6 flex">
              Role Name <span>*</span>
            </label>
            <TextField
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Add Role Name"
              autoComplete="off"
              disabled={mode === 'view'}
            />
          </div>

          <div className="flex flex-column">
            <label className="mb-6">
              Is Active <span>*</span>
            </label>
            <RadioGroup
              name="active-status"
              className="flex flex-row align-items-center"
              value={isActive}
              onChange={(e) => setIsActive(e.target.value)}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Yes"
                className="mr-10"
                disabled={mode === 'view'}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="No"
                disabled={mode === 'view'}
              />
            </RadioGroup>
          </div>
        </div>

        <div className="modal-footer flex justify-content-center align-items-center">
          {closeText && (
            <Button onClick={handleClose} className={singleBtn ? "primary-btn" : "border-btn"}>
              {closeText}
            </Button>
          )}
          {submitText && (
            <Button className="primary-btn" onClick={handleSubmit} disabled={mode === 'view'}>
              {submitText}
            </Button>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default memo(AddRoleModal);
